import {
  browserName,
  getUA,
  isAndroid,
  isChrome,
  isIOS,
  isIOS13,
  isSafari,
} from "react-device-detect";

/**
 * 브라우저 실행 환경 판별을 전담한다. 도메인 지식이 없는 순수 모듈이다.
 *
 * 의존성을 react-device-detect 하나로 제한한 것은 의도적이다.
 * token-cookie.ts가 이 모듈을 쓰는데, 그쪽은 axios-client.ts(소비 모듈 40여 개)의
 * 의존성 그래프를 좁게 유지해야 해서 js-cookie / react-device-detect 둘만 허용한다.
 */

/** iOS 여부. iPadOS 13+ 는 UA가 Macintosh로 나오므로 isIOS13까지 봐야 한다. */
export function isIosDevice() {
  return isIOS || isIOS13;
}

export function isAndroidDevice() {
  return isAndroid;
}

// standalone만 보면 manifest의 display가 바뀌었을 때 조용히 죽는다.
const STANDALONE_DISPLAY_MODES = ["standalone", "fullscreen", "minimal-ui"];

/** 홈 화면에 설치된 PWA로 실행 중인지. */
export function isStandaloneDisplayMode() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }

  const matchesDisplayMode = STANDALONE_DISPLAY_MODES.some(
    (mode) => window.matchMedia(`(display-mode: ${mode})`).matches
  );

  // iOS 16.3 이하는 apple-mobile-web-app-capable로 standalone 실행되면서도
  // display-mode 미디어 쿼리가 false를 반환한다. 그때는 이쪽이 유일한 신호다.
  const isIosLegacyStandalone =
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
    true;

  return matchesDisplayMode || isIosLegacyStandalone;
}

export type InAppBrowserKind =
  | "kakaotalk"
  | "kakaostory"
  | "naver"
  | "instagram"
  | "facebook"
  | "line"
  | "daum"
  | "band"
  | "everytime"
  | "coupang";

/**
 * 대소문자를 무시한다. ua-parser-js가 카카오를 `/(kakao(?:talk|story))[\/ ]/i`로
 * 잡는 것에서 알 수 있듯 실제 UA에는 KAKAOTALK / KakaoTalk 같은 표기 변형이 있다.
 *
 * 이 목록의 정확도가 배너 노출 여부를 좌우하지는 않는다 — 노출은
 * getMobileBrowserKind()의 화이트리스트가 결정하고, 여기서 놓치면 그쪽에서
 * "webview"로 떨어져 어차피 배너가 뜨지 않는다. 이 값은 계측용이다.
 */
const IN_APP_BROWSER_MARKERS: [InAppBrowserKind, string[]][] = [
  // kakaostory가 kakaotalk보다 먼저 와야 한다 — 스토리 UA에 둘 다 들어 있는 경우가 있다.
  ["kakaostory", ["kakaostory"]],
  ["kakaotalk", ["kakaotalk"]],
  ["naver", ["naver(", "; inapp;"]],
  ["instagram", ["instagram"]],
  ["facebook", ["fb_iab", "fban/", "fbav/"]],
  // 슬래시까지 포함해야 한다. "line" 단독으로는 오탐이 너무 넓다.
  ["line", ["line/"]],
  ["daum", ["daum/", "daumapps"]],
  ["band", ["band/"]],
  ["everytime", ["everytimeapp"]],
  ["coupang", ["coupang"]],
];

/** 어느 앱의 인앱 브라우저인지. 계측용이며 정식 브라우저면 null. */
export function getInAppBrowserKind(): InAppBrowserKind | null {
  const ua = getUA?.toLowerCase();
  if (!ua) {
    return null;
  }

  // 웨일은 정식 브라우저인데 UA에 네이버 문자열을 가질 수 있다.
  const isWhale = ua.includes("whale");

  for (const [kind, markers] of IN_APP_BROWSER_MARKERS) {
    if (kind === "naver" && isWhale) {
      continue;
    }
    if (markers.some((marker) => ua.includes(marker))) {
      return kind;
    }
  }

  return null;
}

/** iOS에서 자체 UA 토큰을 갖는 정식 서드파티 브라우저(모두 WebKit 기반). */
const IOS_THIRD_PARTY_BROWSER_MARKERS = [
  "crios/",
  "fxios/",
  "edgios/",
  "opios/",
  "opt/",
  "whale/",
];

/** 안드로이드에서 ua-parser-js가 이름으로 식별하는 정식 브라우저. */
const ANDROID_BROWSER_NAMES = [
  "Samsung Internet",
  "Samsung Browser",
  "Edge",
  "Firefox",
  "Opera",
  "Opera Mobi",
  "Opera Mini",
  "Whale",
  "Chromium",
  "MIUI Browser",
  "Yandex",
  "DuckDuckGo",
  "Brave",
];

export type MobileBrowserKind =
  /** 홈 화면 추가로 standalone 실행이 되는 유일한 iOS 브라우저 */
  | "ios-safari"
  /** iOS 정식 브라우저이지만 사파리가 아님 → 사파리로 유도 */
  | "ios-other"
  /** 안드로이드에서 WebAPK 설치가 되는 브라우저 */
  | "android-chrome"
  /** 안드로이드 정식 브라우저이지만 크롬이 아님 → 크롬으로 유도 */
  | "android-other"
  /** 인앱 브라우저(WebView) — 설치 경로가 없다 */
  | "webview"
  /** 판별 불가 */
  | "unknown";

/**
 * 배너 노출·문구를 결정하는 단일 판별.
 *
 * 인앱 목록을 블랙리스트로 쓰지 않고 정식 브라우저를 화이트리스트로 확인한다.
 * 카카오 인앱만 해도 표기 변형(KAKAOTALK / KakaoTalk)과 바텀시트 같은 변종이 있어
 * 목록으로 막으면 하나 새는 순간 설치 안내가 엉뚱한 곳에 뜬다. 반대로 뒤집으면
 * 판별에 실패한 모든 경우가 "webview" / "unknown"으로 떨어져 조용히 배너가 없다.
 *
 * 근거가 되는 실측: iOS 순수 WKWebView는 browserName이 "WebKit"(사파리는
 * "Mobile Safari"), 안드로이드 WebView는 "Chrome WebView"(크롬은 "Chrome")로
 * 갈리므로 마커 없이도 웹뷰와 정식 브라우저가 구분된다.
 */
export function getMobileBrowserKind(): MobileBrowserKind {
  const ua = getUA?.toLowerCase() ?? "";

  if (isIosDevice()) {
    // isSafari는 이름 기준(Safari | Mobile Safari)이라 iPad 데스크톱 모드도 잡는다.
    if (isSafari) {
      return "ios-safari";
    }
    if (IOS_THIRD_PARTY_BROWSER_MARKERS.some((marker) => ua.includes(marker))) {
      return "ios-other";
    }
    // 남은 것은 WKWebView("WebKit") 계열 = 인앱.
    return "webview";
  }

  if (isAndroid) {
    if (isChrome) {
      return "android-chrome";
    }
    if (ANDROID_BROWSER_NAMES.includes(browserName)) {
      return "android-other";
    }
    // "Chrome WebView" 및 이름 미상은 전부 인앱으로 본다.
    return "webview";
  }

  return "unknown";
}

